/**
 * FE-24: Suggestions Section — Top ops + product suggestions on Dashboard.
 * Displays up to 5 items per category with level badges and action links.
 */

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchSuggestions } from "../api/admin-suggestions";
import type {
  Suggestion,
  SuggestionLevel,
  SuggestionsResult,
} from "../api/admin-suggestions";
import { t } from "../i18n";
import * as shared from "../styles/shared";

const MAX_ITEMS = 5;

// ─── Main Section ───

export function SuggestionsSection(): React.JSX.Element {
  const { data, loading, error } = useSuggestionsData();

  if (loading)
    return <p style={shared.loadingText}>{t("suggestions.loading")}</p>;
  if (error) return <p style={shared.errorBanner}>{error}</p>;
  if (!data) return <></>;

  return (
    <section style={shared.section}>
      <h2 style={shared.sectionTitle}>{t("suggestions.title")}</h2>
      <div style={s.columns}>
        <SuggestionList
          title={t("suggestions.ops")}
          items={data.ops.slice(0, MAX_ITEMS)}
        />
        <SuggestionList
          title={t("suggestions.product")}
          items={data.product.slice(0, MAX_ITEMS)}
        />
      </div>
    </section>
  );
}

// ─── List ───

function SuggestionList({
  title,
  items,
}: {
  title: string;
  items: ReadonlyArray<Suggestion>;
}): React.JSX.Element {
  return (
    <div style={s.listColumn}>
      <h3 style={s.listTitle}>{title}</h3>
      {items.length === 0 && (
        <p style={s.empty}>{t("suggestions.no_suggestions")}</p>
      )}
      {items.map((item) => (
        <SuggestionCard key={item.id} suggestion={item} />
      ))}
    </div>
  );
}

// ─── Card ───

function SuggestionCard({
  suggestion,
}: {
  suggestion: Suggestion;
}): React.JSX.Element {
  return (
    <div style={s.card}>
      <div style={s.cardHeader}>
        <LevelBadge level={suggestion.level} />
        <span style={s.cardTitle}>{suggestion.title}</span>
      </div>
      <p style={s.cardDetail}>{suggestion.detail}</p>
      <Link to={suggestion.link_to} style={s.viewLink}>
        {t("common.view")}
      </Link>
    </div>
  );
}

// ─── Level Badge ───

const LEVEL_COLORS: Record<SuggestionLevel, string> = {
  P1: "#dc2626",
  P2: "#f59e0b",
  P3: "#6b7280",
};

function LevelBadge({
  level,
}: {
  level: SuggestionLevel;
}): React.JSX.Element {
  return (
    <span style={{ ...shared.badge, background: LEVEL_COLORS[level] }}>
      {level}
    </span>
  );
}

// ─── Data Hook ───

function useSuggestionsData(): {
  data: SuggestionsResult | null;
  loading: boolean;
  error: string | null;
} {
  const [data, setData] = useState<SuggestionsResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchSuggestions());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t("common.failed_to_load"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, loading, error };
}

// ─── Styles ───

const s: Record<string, React.CSSProperties> = {
  columns: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 24,
  },
  listColumn: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  listTitle: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 4,
  },
  empty: {
    fontSize: 13,
    color: "var(--color-text-secondary)",
    padding: "12px 0",
  },
  card: {
    padding: 14,
    border: "1px solid var(--color-border)",
    borderRadius: 8,
    background: "var(--color-bg)",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 6,
  },
  cardTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--color-text)",
  },
  cardDetail: {
    fontSize: 12,
    color: "var(--color-text-secondary)",
    margin: "0 0 8px",
    lineHeight: 1.5,
  },
  viewLink: {
    fontSize: 12,
    fontWeight: 500,
    color: "var(--color-primary)",
    textDecoration: "none",
  },
};
